import { AxiosMediaUploadInstance } from '@/lib/axios/axios-media-upload-instance';
import { PlanPriceEdge, PlanSubscription, PlanTypeEnum } from '@/types/generated/graphql';

export function getTimeZone(date: Date | string) {
  const timeZone = 'UTC';
  if (!timeZone) return new Date(date);
  return new Date(new Date(date).toLocaleString('en', { timeZone }));
}

export const formatDate = (isoString: string): string => {
  const date = getTimeZone(isoString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  return date.toLocaleDateString('en-US', options);
};

export const getPlanTypeBySub = (subscription: PlanSubscription | null): PlanTypeEnum => {
  const slugId = subscription?.subscriptionItems?.[0]?.itemPriceId?.split('-')?.[2];
  switch (slugId) {
    case 'premium':
      return PlanTypeEnum.business;
    case 'basic':
      return PlanTypeEnum.basic;
    case 'custom':
      return PlanTypeEnum.custom;
    default:
      return PlanTypeEnum.basic;
  }
};

export const getPlanNameByType = (planType: PlanTypeEnum): string => {
  switch (planType) {
    case PlanTypeEnum.business:
      return 'Premium';
    case PlanTypeEnum.basic:
      return 'Standard';
    case PlanTypeEnum.custom:
      return 'Custom';
    default:
      return 'Basic';
  }
};

export const pluralize = (count: number, singular: string, suffix = 's') => {
  return `${count} ${count === 1 ? singular : singular + suffix}`;
};

export const userPluralize = (count: number | null | undefined) => {
  if (count == undefined) return '0 Users';
  return pluralize(count, 'User');
};

export const centsToDollar = (cents: number | null | undefined | string): number => {
  if (typeof cents == 'string') cents = parseInt(cents);

  if (cents == null) return 0;
  return cents / 100;
};

export const dollarsToCents = (dollars: number | null | undefined | string): number => {
  if (dollars == null) return 0;
  const value = typeof dollars === 'string' ? Number(dollars) : dollars;
  if (Number.isNaN(value)) return 0;
  return Math.round(value * 100);
};

export const getRoundedAmount = (amount: number) => {
  return Math.ceil(amount * 100) / 100;
};

export const formatAmount = (amount?: number | null | string, currency: string = 'USD'): string => {
  if (typeof amount == 'string') amount = parseInt(amount);
  if (amount == undefined) amount = 0;
  const twoDecimalAmount = getRoundedAmount(amount);

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(twoDecimalAmount);
};

export const getFormattedWalletAmount = (amount?: number | null | string, currency: string = 'USD'): string => {
  if (typeof amount == 'string') amount = parseInt(amount);
  if (amount == undefined) amount = 0;
  const fourDecimalAmount = Math.round(amount * 10000) / 10000;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(fourDecimalAmount);
};

export const detectCurrentSubscriptionType = (item: PlanSubscription) => {
  return item?.subscriptionItems?.some((subItem) => {
    return subItem?.itemPriceId?.includes('custom');
  });
};

export const getPlanTypeByPlanPrice = (item: PlanPriceEdge | null) => {
  const targetedPlanPriceType = item?.node?.plan?.slug?.split('-')?.[2];
  return targetedPlanPriceType === 'basic' ? PlanTypeEnum.basic : PlanTypeEnum.business;
};

export const SWITCH_PLAN_MAPPING = {
  1: 2,
  2: 1,
  3: 4,
  4: 3,
};

/**
 * Truncate a string by character length.
 *
 * @param text - The input string.
 * @param limit - Maximum allowed characters (default 200).
 * @param suffix - String to append when truncated (default '...').
 * @returns The truncated or original string.
 */
export function truncateString(text?: string | null, limit: number = 200, suffix: string = '...'): string {
  if (typeof text !== 'string') return '';
  if (text.length <= limit) return text;
  return text.slice(0, limit).trimEnd() + suffix;
}

/**
 * Truncate the middle of a string.
 * Example: "abcdefghijk.jpg" → "abc...ijk.jpg"
 *
 * @param text - The input string.
 * @param limit - Total max length allowed.
 * @param suffix - The middle replacement (default '...').
 */
export function truncateMiddle(text?: string | null, limit: number = 20, suffix: string = '...'): string {
  if (typeof text !== 'string') return '';
  if (text.length <= limit) return text;

  const suffixLength = suffix.length;
  if (limit <= suffixLength) return suffix; // not enough room

  const charsToShow = limit - suffixLength;
  const front = Math.ceil(charsToShow / 2);
  const back = Math.floor(charsToShow / 2);

  return text.slice(0, front) + suffix + text.slice(text.length - back);
}

export const formatPlanAmount = (amount: number, period: string) =>
  (period === 'year' ? amount / 1200 : amount / 100).toFixed(0);

export async function getUploadedAudioId(url: string) {
  const blob = await fetch(url).then((r) => r.blob());
  const formData = new FormData();
  formData.append('file', blob, 'audio.mp3');

  const audioUpload = await AxiosMediaUploadInstance.post('/upload/api/v1/media/single', formData, {
    headers: {
      'Content-Type': 'audio/mpeg',
    },
  }).catch((err) => {
    console.error(err);
  });

  return audioUpload?.data?.data?.id as number;
}
